import type { ReactNode } from "react";
import { ShieldAlert, AlertCircle } from "lucide-react";
import { ApiError } from "@/lib/api";

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-[13px] bg-red-50/80 border-2 border-red-300/60 rounded-xl px-4 py-3">
      <ShieldAlert className="size-5 text-red-500 shrink-0 mt-0.5" />
      <div>
        <p className="font-bold text-red-700 mb-0.5">{title}</p>
        <p className="text-red-600/80 text-[12px] leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/** A generation error: the content cards, or a short line. Image-file errors open UnsupportedFileModal instead. */
export function NsfwOrError({ message, error }: { message?: string; error?: unknown }) {
  const code = error instanceof ApiError ? error.code : undefined;
  message ??= error instanceof Error ? error.message : undefined;
  if (code === "UNSUPPORTED_IMAGE" || code === "IMAGE_TOO_LARGE") return null;

  if (code === "MODERATION_BLOCKED") {
    return (
      <Card title="This image or prompt can't be used">
        Our safety checks flagged this request. CartoonTale does not allow content that appears to involve minors or that breaks our content rules. Please try a different image or prompt.
      </Card>
    );
  }

  const isNsfw = code === "NSFW_CONTENT" || message?.includes("acceptable use policy") || message?.includes("NSFW");
  if (isNsfw) {
    return (
      <Card title="Content not allowed">
        Your prompt was flagged by our content filter. CartoonTale does not allow NSFW, violent, hateful, or otherwise prohibited content. Please revise your prompt and try again.
      </Card>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-red-400">
      <AlertCircle className="size-3.5 shrink-0" />
      {message || "Something went wrong. Please try again."}
    </div>
  );
}
